import React, { useState } from 'react';
import { Button, Alert } from '@/components/ui';

interface BoqImportUploaderProps {
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
}

export function BoqImportUploader({ onFileSelect, isLoading }: BoqImportUploaderProps) {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
    }
  };

  return (
    <div className="space-y-6">
      <Alert variant="info" icon="ri-information-line">
        Hệ thống hỗ trợ import BOQ từ file Excel (.xlsx, .xls). Vui lòng đảm bảo các cột dữ liệu (Mã hiệu, Tên hạng mục, Đơn vị, Khối lượng, Đơn giá...) khớp với mẫu chuẩn để việc mapping diễn ra chính xác.
      </Alert>

      <div 
        className={`
          relative flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-12 transition-all cursor-pointer
          ${dragActive ? 'border-os-primary bg-os-primary/5 scale-[0.99]' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-upload')?.click()}
      >
        <input 
          id="file-upload"
          type="file" 
          className="hidden" 
          accept=".xlsx, .xls"
          onChange={handleChange}
        />
        
        <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-4">
          <i className="ri-file-excel-2-fill text-4xl text-emerald-500" />
        </div>

        <h3 className="text-lg font-bold text-slate-900 mb-1">
          Tải lên file dự toán (BOQ)
        </h3>
        <p className="text-sm text-slate-500 text-center max-w-xs">
          Kéo thả file vào đây hoặc <span className="text-os-primary font-semibold">nhấn để chọn file</span> từ máy tính của bạn.
        </p>
        
        <div className="mt-6 flex gap-3">
          <Button variant="outline" size="sm" icon="ri-download-line">Tải file mẫu (.xlsx)</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: 'ri-check-line', title: 'Tự động Mapping', desc: 'Hệ thống tự động nhận diện các cột dữ liệu.' },
          { icon: 'ri-calculator-line', title: 'Tính toán lại', desc: 'Tự động tính tổng thành tiền, chi phí vật tư, nhân công.' },
          { icon: 'ri-shield-check-line', title: 'Kiểm tra lỗi', desc: 'Cảnh báo nếu thiếu thông tin hoặc sai định mức.' }
        ].map((item, idx) => (
          <div key={idx} className="p-4 bg-white border border-slate-100 rounded-xl flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
              <i className={`${item.icon} text-slate-400`} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900">{item.title}</h4>
              <p className="text-xs text-slate-500">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
